/**
 * Combat-end detection (B09).
 *
 * Combat ends when either:
 *   1. Every engaged enemy is dead — CombatManager handles this path
 *      instantly (see the `anyAlive` short-circuit).
 *   2. **No enemy has been in COMBAT or ATTACKING state for `window`
 *      seconds.** This covers the "player walked away and all pursuers
 *      gave up" path, and the "enemies fled" path (B08).
 *
 * This helper is pure bookkeeping — it only tracks elapsed quiet time.
 * The caller decides what "engaged" means and what to do when the timer
 * trips.
 */

export class CombatEndWatcher {
  private quietSeconds = 0;

  constructor(public readonly window: number) {}

  /**
   * Feed one tick of elapsed time plus the number of currently-engaged
   * enemies (those in COMBAT/ATTACKING state). Returns true on the tick
   * the quiet window first expires so callers can fire the transition
   * exactly once; subsequent calls return false until `reset()`.
   */
  tick(deltaSeconds: number, engagedCount: number): boolean {
    if (engagedCount > 0) {
      this.quietSeconds = 0;
      return false;
    }
    const wasBelow = this.quietSeconds < this.window;
    this.quietSeconds += Math.max(0, deltaSeconds);
    return wasBelow && this.quietSeconds >= this.window;
  }

  /** True once the quiet window has elapsed. */
  get expired(): boolean {
    return this.quietSeconds >= this.window;
  }

  /** Elapsed quiet seconds since the last engaged tick. */
  get elapsed(): number {
    return this.quietSeconds;
  }

  reset(): void {
    this.quietSeconds = 0;
  }
}
